import './logs-page.scss';
import React, { useEffect, useMemo, useState } from 'react';
import CustomButton from '../../../shared/custom-button/custom-button';
import SearchIcon from '@svg/search.svg?react';
import LogsSearch from '@svg/logs-search.svg?react';
import { Input } from 'antd';
import { InsightsSubMenu, LogsType, MainPages } from '@src/utils/enums';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../../store';
import { Search } from './search/search';
import { trafficFilteringCriteriaConfig } from './traffic-filtering-criteria-config';
import { urlFilteringCriteriaConfig } from './url-filtering-criteria-config';
import { VirtualizedTable } from '../virtualized-table/virtualized-table';
import { ExportLogs } from './export/export-logs';
import CustomIcon, { IconType } from '../../../shared/custom-icon/custom-icon';
import useCustomModal from '../../../shared/custom-modal/custom-modal';
import { ExportAllModal } from './export/export-all-modal';
import { getThreatFilteringCriteriaConfig } from './threat-filtering-criteria-config';
import { LogCriteria } from '@src/dtos/search-criteria.dto';
import { EmptyState } from '../../../shared/empty-state/empty-state';
import { usePageViewEvent } from '../../../shared/hooks/use-page-view-event';

export const LogsPage: React.FC = observer(() => {
    const { logsStore, uiStore, fwVersionStore, customerStore } = useStores();
    const [expandSearch, setExpandSearch] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filteredLogs, setFilteredLogs] = useState(logsStore.logsByType);
    const [showModal, , contextHolder] = useCustomModal();

    usePageViewEvent(MainPages.INSIGHTS, InsightsSubMenu.LOGS);

    useEffect(() => {
        logsStore.setActiveSubMenu(InsightsSubMenu.LOGS);
    }, [logsStore, uiStore]);

    useEffect(() => {
        const searchShouldBeOpened = !logsStore.logsByType.length;
        setExpandSearch(searchShouldBeOpened);
    }, [logsStore.logsByType, logsStore.selectedLogsType, logsStore]);

    useEffect(() => {
        let newFilteredLogs = [...logsStore.logsByType];
        newFilteredLogs = newFilteredLogs.filter(log => {
            return Object.values(log).some(value =>
                value.toString().toLowerCase().includes(searchText.toLowerCase())
            );
        });
        setFilteredLogs([...newFilteredLogs]);
    }, [searchText, logsStore.logsByType]);

    const exportAll = (logsTypesToExport: LogsType[]) => {
        showModal({
            wrapClassName: 'export-all-modal-wrapper',
            width: 1140,
            title: 'Export Logs',
            body: (onClose) =>
                <ExportAllModal logTypesToExport={logsTypesToExport}
                                activeVersion={fwVersionStore.activeVersionData}
                                onClose={onClose}
                />,
            hideConfirmBtns: true
        });
    };

    // When Threat log type is selected, force Threat Type filter to DNS and disable editing
    const isThreatTypeForcedDns = logsStore.selectedLogsType === LogsType.THREAT && customerStore.isAdvancedConnection;

    // Set criteria config accordingly
    const getCriteriaConfig = useMemo(() => {
        switch (logsStore.selectedLogsType) {
            case LogsType.URL_FILTERING:
                return urlFilteringCriteriaConfig;
            case LogsType.TRAFFIC:
                return trafficFilteringCriteriaConfig(fwVersionStore.activeVersionData);
            case LogsType.THREAT:
                return getThreatFilteringCriteriaConfig(isThreatTypeForcedDns);
        }
    }, [fwVersionStore.activeVersionData, logsStore.selectedLogsType, isThreatTypeForcedDns]);

    // Force search criteria for Threat Type to DNS if needed
    useEffect(() => {
        if (isThreatTypeForcedDns) {
            logsStore.setSearchCriteria({
                ...logsStore.currentSearchCriteria,
                threatTypes: ['DNS']
            });
        }
    }, [isThreatTypeForcedDns, logsStore]);

    const handleExportAllClick = (hasPermissions: boolean) => {
        const logTypes = hasPermissions
            ? [LogsType.TRAFFIC, LogsType.URL_FILTERING, LogsType.THREAT]
            : [LogsType.TRAFFIC, LogsType.URL_FILTERING];

        exportAll(logTypes);
    };

    const LogsEmptyState = () => {
        return <EmptyState
            iconType={IconType.LogsEmptyState}
            title="What logs are you searching for?"
            subTitle="Set the filters to display the logs and click Search."/>;
    };

    function onLogTypeClick(type: LogsType) {
        return () => logsStore.selectLogType(type);
    }

    const logCount = useMemo(() => {
        return filteredLogs.length;
    }, [filteredLogs]);

    return <div className="reports-logs-page" data-testid={'logs-page'}>
        <div className="logs-header">
            <div className="header-page">
                <div className="left-header">
                    <div className="radio-buttons">
                        {[LogsType.TRAFFIC, LogsType.URL_FILTERING, LogsType.THREAT].map(type =>
                            <CustomButton key={type} label={type}
                                          className={`${logsStore.selectedLogsType === type && 'active'} filter-btn`}
                                          onClick={onLogTypeClick(type)} isDisabled={false}/>
                        )}
                    </div>
                </div>
                <div className="right-header">
                    <div className="total-results">Total: {logCount}</div>
                    <Input className={'filter-results'} placeholder={'Filter Results'} value={searchText}
                           onChange={(e) => setSearchText(e.target.value)}
                           allowClear prefix={<SearchIcon/>}/>
                    <CustomButton label={''} className={`logs-search secondary-btn ${expandSearch && 'active'}`}
                                  prefixIcon={<LogsSearch/>} onClick={() => setExpandSearch(!expandSearch)}/>
                    <CustomButton label={'Export All'} className={'export-all'}
                                  prefixIcon={<CustomIcon type={IconType.EXPORT}/>}
                                  onClick={() => handleExportAllClick(true)}/>
                    <ExportLogs/>
                </div>
            </div>
            {<Search<LogCriteria> key={logsStore.selectedLogsType}
                                  show={expandSearch}
                                  isLoading={!!logsStore.isLoadingByTypeMap.get(logsStore.selectedLogsType) || !fwVersionStore.activeVersionData}
                                  criteriaConfig={getCriteriaConfig}
                                  setSearchCriteria={(searchCriteria) => {
                                      logsStore.setSearchCriteria(searchCriteria);
                                  }}
                                  currentSearchCriteria={logsStore.currentSearchCriteria}
                                  onSearch={() => logsStore.load()}/>}
        </div>
        <div className={`logs-table`}>
            <VirtualizedTable key={logsStore.selectedLogsType}
                              content={filteredLogs}
                              isInitialState={logsStore.isInitialState}
                              columnsConfig={logsStore.columnConfigByType}
                              searchText={searchText}
                              isLoading={!!logsStore.isLoadingByTypeMap.get(logsStore.selectedLogsType)}
                              noResultIcon={IconType.LogsLockedState}
                              emptyStateComp={<LogsEmptyState/>}
            />
        </div>
        {contextHolder}
    </div>;
});
